import { FC, useEffect, useMemo, useState } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { Box, Tab, Tabs } from '@mui/material';

import { SecondaryButton } from '@/components/Shared/Buttons';
import { useGetMySDKUser } from '@/hooks';

import { useGetTokenUsageReport } from '../hooks/useGetTokenUsageReport';
import { aggregateUsageByUser, downloadCsv, toCsv } from '../utils';

import { SDKUsageByUserGrid } from './SDKUsageByUserGrid';
import { SDKUsageGrid } from './SDKUsageGrid';

type UsageView = 'project' | 'user';

export const SDKUsagePage: FC = () => {
  const navigate = useNavigate();
  const { data: sdkUser } = useGetMySDKUser();
  const organizationId = sdkUser?.organizationId ?? '';

  const [fromDate, setFromDate] = useState<Dayjs>(dayjs().subtract(6, 'day').startOf('day'));
  const [toDate, setToDate] = useState<Dayjs>(dayjs().endOf('day'));

  const [view, setView] = useState<UsageView>('project');

  const { mutate: fetchUsageReport, data: tokenUsage } = useGetTokenUsageReport();

  const userUsage = useMemo(() => aggregateUsageByUser(tokenUsage ?? []), [tokenUsage]);

  const changeFromDate = (newValue: Dayjs) => {
    const newFrom = newValue.startOf('day');
    setFromDate(newFrom);
    if (newFrom.isAfter(toDate)) setToDate(newValue.endOf('day'));
  };

  const changeToDate = (newValue: Dayjs) => {
    const newTo = newValue.endOf('day');
    setToDate(newTo);
    if (newTo.isBefore(fromDate)) setFromDate(newValue.startOf('day'));
  };

  const exportCsv = () => {
    const period = `${fromDate.format('YYYY-MM-DD')}_${toDate.format('YYYY-MM-DD')}`;

    if (view === 'user') {
      const csv = toCsv(
        ['User', 'Total usage (tokens)', 'Simulations', 'Projects', 'Last completed'],
        userUsage.map((row) => [
          row.email,
          row.totalUsage.toFixed(2),
          row.simulationCount,
          row.projectCount,
          row.lastCompleted != null ? dayjs(row.lastCompleted).format('YYYY-MM-DD HH:mm') : '',
        ])
      );
      downloadCsv(`sdk-token-usage-per-user_${period}.csv`, csv);
      return;
    }

    const csv = toCsv(
      [
        'Project',
        'Simulation ID',
        'Simulation name',
        'Usage (tokens)',
        'Runtime (s)',
        'GPU count',
        'Completed',
        'Run by',
      ],
      (tokenUsage ?? []).flatMap((project) =>
        (project.simulationUsages ?? []).map((simulation) => [
          project.projectName,
          simulation.simulationId,
          simulation.simulationName,
          simulation.usage.toFixed(2),
          simulation.runtime != null ? Math.floor(simulation.runtime) : '',
          simulation.maxGPUCount,
          simulation.completedAt ? dayjs(simulation.completedAt).format('YYYY-MM-DD HH:mm') : '',
          simulation.email,
        ])
      )
    );
    downloadCsv(`sdk-token-usage-per-simulation_${period}.csv`, csv);
  };

  useEffect(() => {
    if (!organizationId || fromDate.isAfter(toDate)) return;
    fetchUsageReport({
      organizationId,
      from: fromDate.toISOString(),
      to: toDate.toISOString(),
    });
  }, [organizationId, fromDate, toDate, fetchUsageReport]);

  return (
    <Box component="div" sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box component="div" display="flex" alignItems="center" justifyContent="space-between">
        <Box component="div" display="flex" alignItems="center" gap={2}>
          <SecondaryButton label="← Back" onClick={() => navigate('/administration')} width={80} />
          <p style={{ fontWeight: '600' }}>SDK token usage</p>
        </Box>
        <Box component="div" display="flex" alignItems="center" gap={2}>
          <SecondaryButton label="Export CSV" disabled={!tokenUsage?.length} onClick={exportCsv} width={120} />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="From date"
              value={fromDate}
              onChange={(newValue) => newValue?.isValid() && changeFromDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', sx: { width: 220 } } }}
            />
            <DatePicker
              label="To date"
              value={toDate}
              onChange={(newValue) => newValue?.isValid() && changeToDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', sx: { width: 220 } } }}
            />
          </LocalizationProvider>
        </Box>
      </Box>
      <Tabs value={view} onChange={(_, newView: UsageView) => setView(newView)}>
        <Tab value="project" label="By project" />
        <Tab value="user" label="By user" />
      </Tabs>
      <Box component="div" flex="1 1 auto">
        {view === 'project' ? (
          <SDKUsageGrid tokenUsage={tokenUsage ?? []} />
        ) : (
          <SDKUsageByUserGrid userUsage={userUsage} />
        )}
      </Box>
    </Box>
  );
};
